import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CardContent, Stack, IconButton, Button, Tabs, Tab } from '@mui/material';
import {
  LocationOn as LocationIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  PlaylistAdd as PlaylistAddIcon
} from '@mui/icons-material';
import WithPoolModal from '../CandidateSourcing/WithPoolModal';
import { useCandidatePoolModal } from '../CandidateSourcing/hooks/useCandidatePoolModal';
import PoolItem from '../CandidateSourcing/PoolItem';
import { ICandidateGroup } from './types';
import ExperienceGroup from './ExperienceGroup';
import EducationItem from './EducationItem';
import SourceIconLink from '../Components/CustomUIElements/SourceIconLink';
import StyledChip from '../CandidateSourcing/StyledChip';
import AddToCandidatePool from '../CandidateSourcing/AddToCandidatePool';
import { classes as genericTableClasses } from '../Components/GenericTable/styles';
import { groupExperienceByCompany } from '../Components/Utilities/groupExperienceByCompany';
import { HeaderCard, SidebarCard, StyledCard, styles } from './styles';
import Documents from './Documents';
import ContactInformation from './ContactInformation';
import Highlights from './Highlights';

export default function CandidateGroup({
  group,
  refetchCandidates,
  allGroups = [],
  currentIndex = -1,
  onNavigate
}: {
  group: ICandidateGroup;
  refetchCandidates: () => void;
  allGroups?: ICandidateGroup[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
}) {
  const candidatePoolModalHook = useCandidatePoolModal();
  const { handlePoolClick } = candidatePoolModalHook;
  const [activeTab, setActiveTab] = useState(0);
  const [isAddToPoolOpen, setIsAddToPoolOpen] = useState(false);
  const [selectedCandidateGroups, setSelectedCandidateGroups] = useState<ICandidateGroup[]>([
    group
  ]);
  const detailsRef = useRef<HTMLDivElement>(null);
  const highlightsRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<HTMLDivElement>(null);
  const educationRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    setSelectedCandidateGroups([group]);
  }, [group]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const refs = [detailsRef, highlightsRef, experienceRef, educationRef];
    const targetRef = refs[newValue];
    if (targetRef?.current && scrollableContainerRef?.current) {
      isScrollingRef.current = true;
      const container = scrollableContainerRef.current;
      const target = targetRef.current;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
      container.scrollTo({
        top: scrollTop - 20,
        behavior: 'smooth'
      });
      setTimeout(() => {
        isScrollingRef.current = false;
        setActiveTab(newValue);
      }, 500);
    }
  };

  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const scrollTop = container.scrollTop;
      const threshold = 200;

      const refs = [
        { ref: detailsRef, index: 0 },
        { ref: highlightsRef, index: 1 },
        { ref: experienceRef, index: 2 },
        { ref: educationRef, index: 3 }
      ];

      let activeIndex = 0;

      for (let i = refs.length - 1; i >= 0; i--) {
        const { ref, index } = refs[i];
        if (ref.current) {
          const elementTop = ref.current.offsetTop;
          if (scrollTop + threshold >= elementTop) {
            activeIndex = index;
            break;
          }
        }
      }

      setActiveTab(activeIndex);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [group]);

  const handleAddToPoolClick = () => {
    setSelectedCandidateGroups([group]);
    setIsAddToPoolOpen(true);
  };
  const handleRemoveCandidateGroup = (candidateGroupId: number) => {
    setSelectedCandidateGroups((prev) =>
      prev.filter((candidateGroup) => candidateGroup.id !== candidateGroupId)
    );
  };
  useEffect(() => {
    if (!selectedCandidateGroups.length) {
      setIsAddToPoolOpen(false);
    }
  }, [selectedCandidateGroups]);

  const handleGoBack = () => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleGoNext = () => {
    if (currentIndex >= 0 && currentIndex < allGroups.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  const canGoBack = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < allGroups.length - 1;
  const experienceGroups = groupExperienceByCompany(group.experience || []);
  const education = group.education || [];

  return (
    <Box sx={styles.container}>
      <WithPoolModal candidatePoolModalHook={candidatePoolModalHook}>
        <HeaderCard>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.headerStack}
          >
            <Stack direction="column" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" gap={2}>
                <Typography sx={{ fontWeight: 700, color: '#084D6D', fontSize: '24px' }}>
                  {group.name}
                </Typography>
                {group.candidates.map((candidate) => (
                  <SourceIconLink
                    key={candidate.id}
                    source={candidate.source}
                    url={candidate.url}
                  />
                ))}
              </Stack>
              <Typography variant="body1" sx={{ ...styles.sectionContent, mb: 2 }}>
                {group.position || 'Software Engineer'}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <Button
                onClick={handleAddToPoolClick}
                startIcon={<PlaylistAddIcon />}
                sx={{
                  ...genericTableClasses.selectAllWithActionsTooltipButton,
                  background: '#DEF3F2',
                  color: '#084D6D',
                  marginLeft: 0
                }}
              >
                Add to Pool
              </Button>
              <IconButton
                onClick={handleGoBack}
                disabled={!canGoBack}
                sx={{
                  ...genericTableClasses.selectAllWithActionsTooltipButton,
                  background: '#DEF3F2',
                  color: '#084D6D',
                  padding: '10px',
                  opacity: canGoBack ? 1 : 0.5
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleGoNext}
                disabled={!canGoNext}
                sx={{
                  ...genericTableClasses.selectAllWithActionsTooltipButton,
                  background: '#DEF3F2',
                  color: '#084D6D',
                  padding: '10px',
                  opacity: canGoNext ? 1 : 0.5
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
        </HeaderCard>

        <Stack direction="row" sx={{ height: 'calc(100vh - 200px)', alignItems: 'flex-start' }}>
          <Box
            ref={scrollableContainerRef}
            sx={{
              width: '55%',
              paddingRight: '24px',
              overflowY: 'auto',
              height: '100%',
              pr: 3
            }}
          >
            <Stack
              direction="column"
              spacing={3}
              sx={{ minHeight: 'calc(100vh + 700px)', paddingBotton: '20px' }}
            >
              <StyledCard ref={detailsRef}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ ...styles.sectionTitle, mb: 2 }}>
                    Details
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} sx={styles.sectionContent}>
                    <LocationIcon sx={{ color: '#666', fontSize: '20px' }} />
                    <Typography variant="body2">
                      {group.location || 'No location provided'}
                    </Typography>
                  </Box>
                </CardContent>
              </StyledCard>

              <Box ref={highlightsRef}>
                <Highlights group={group} />
              </Box>

              <StyledCard ref={experienceRef}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ ...styles.sectionTitle, mb: 2 }}>
                    Experience
                  </Typography>
                  <Box sx={{ ...styles.sectionContent, position: 'relative' }}>
                    <Box sx={styles.timelineLine} />

                    <Stack direction="column" spacing={3}>
                      {experienceGroups.map((experienceGroup, index) => (
                        <ExperienceGroup key={index} experienceGroup={experienceGroup} />
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </StyledCard>

              <StyledCard ref={educationRef}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ ...styles.sectionTitle, mb: 2 }}>
                    Education
                  </Typography>
                  <Box sx={{ ...styles.sectionContent, position: 'relative' }}>
                    <Stack direction="column" spacing={3} sx={styles.sectionContent}>
                      {education.map((educationItem, index) => (
                        <EducationItem key={index} education={educationItem} />
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </StyledCard>
            </Stack>
          </Box>

          <Stack
            direction="column"
            spacing={3}
            sx={{
              width: '30%',
              paddingRight: '24px',
              height: '100%'
            }}
          >
            <ContactInformation group={group} />
            <Documents assets={group.assets} />

            <SidebarCard>
              <CardContent>
                <Typography variant="h6" sx={styles.sectionTitle}>
                  Candidate pools
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} sx={styles.sectionContent}>
                  {group.candidate_pools.map((pool) => (
                    <PoolItem
                      key={pool.id}
                      pool={pool}
                      onViewPool={() => handlePoolClick(pool.id)}
                    />
                  ))}
                  <StyledChip onClick={handleAddToPoolClick}>
                    <AddIcon />
                  </StyledChip>
                </Box>
              </CardContent>
            </SidebarCard>

            <SidebarCard>
              <CardContent>
                <Typography variant="h6" sx={styles.sectionTitle}>
                  Candidate tags
                </Typography>
                <Box display="flex" flexWrap="wrap" sx={styles.sectionContent}>
                  {group.tags.map((tag) => (
                    <StyledChip key={tag}>{tag}</StyledChip>
                  ))}
                </Box>
              </CardContent>
            </SidebarCard>
          </Stack>
          <Stack
            sx={{
              borderLeft: '1px solid #ddd',
              width: '15%',
              height: '100%',
              position: 'sticky',
              top: 0
            }}
          >
            <Tabs
              orientation="vertical"
              value={activeTab}
              onChange={handleTabChange}
              sx={styles.tabs}
              slotProps={{
                indicator: {
                  sx: styles.tabsIndicator
                }
              }}
            >
              <Tab label="Details" />
              <Tab label="Highlights" />
              <Tab label="Experience" />
              <Tab label="Education" />
            </Tabs>
          </Stack>
        </Stack>
      </WithPoolModal>
      <AddToCandidatePool
        isOpen={isAddToPoolOpen}
        handleClose={() => setIsAddToPoolOpen(false)}
        selectedCandidateGroups={selectedCandidateGroups}
        onRemoveCandidateGroup={handleRemoveCandidateGroup}
        refetch={refetchCandidates}
      />
    </Box>
  );
}
